import type { ApprovalAttributes } from '@/types/approval';

/** F&CO 마케팅 사전 품의 — 레거시 appr_edit_forms_fnco_pre_marketing_ai */
export const FNCO_PRE_MARKETING_TYPE = 221;

export const isFncoPreMarketingType = (apvType: number): boolean =>
  apvType === FNCO_PRE_MARKETING_TYPE;

/** 마케팅 분석 레코드(GET marketing-analysis/{id} 의 data) — 레거시 APV_SUB.CL02 와 같은 모양 */
export type FncoMarketingAnalysis = {
  id?: string;
  result?: { value?: Record<string, unknown> } | null;
  [key: string]: unknown;
};

export type FncoPreMarketingState = {
  analysisId: string;
  /** 조회 전·실패면 null */
  analysis: FncoMarketingAnalysis | null;
};

export type FncoBudgetRow = {
  adType: string;
  landingMedia: string;
  amount: number;
};

export type FncoBudgetGroup = {
  category: string;
  total: number;
  rows: FncoBudgetRow[];
};

export type FncoPreMarketingView = {
  startDate: string;
  endDate: string;
  /** null 이면 「(기안 목적 미기재)」 */
  purpose: string | null;
  summary: string;
  totalCost: number;
  groups: FncoBudgetGroup[];
};

type Rec = Record<string, unknown>;

const asRecord = (v: unknown): Rec | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : null;

const asText = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

/** 레거시 toISOString().split('T')[0] — UTC 날짜라 KST 자정 값은 하루 앞으로 보이지만 EAS 적재(BE substr)와 같아야 함 */
const toLegacyDate = (v: unknown): string => {
  if (typeof v !== 'string' && typeof v !== 'number') return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

export const toFncoPreMarketingView = (
  analysis: FncoMarketingAnalysis | null | undefined,
): FncoPreMarketingView | null => {
  const value = asRecord(asRecord(analysis?.result)?.value);
  if (!value) return null;

  const range = Array.isArray(value.dateRange) ? value.dateRange : [];
  const hasRange = range.length >= 2;
  const purpose = asText(value.purpose);

  const groups: FncoBudgetGroup[] = [];
  const items = Array.isArray(value.budgetItems) ? value.budgetItems : [];
  items.forEach((raw) => {
    const item = asRecord(raw) ?? {};
    const category = asText(item.category) || '-';
    const amount = Number(item.amount || 0);
    let group = groups.find((g) => g.category === category);
    if (!group) {
      group = { category, total: 0, rows: [] };
      groups.push(group);
    }
    group.total += amount;
    group.rows.push({
      adType: asText(item.adType),
      landingMedia: asText(item.landingMedia),
      amount,
    });
  });

  return {
    startDate: hasRange ? toLegacyDate(range[0]) : '',
    endDate: hasRange ? toLegacyDate(range[1]) : '',
    purpose: purpose.trim() ? purpose : null,
    summary:
      asText(value.content) ||
      asText(value.summary) ||
      asText(value.description),
    totalCost: groups.reduce((s, g) => s + g.total, 0),
    groups,
  };
};

export const getFncoAnalysisTitle = (
  analysis: FncoMarketingAnalysis | null | undefined,
): string => asText(asRecord(asRecord(analysis?.result)?.value)?.title).trim();

/** 레거시 Number.toLocaleString() — 한국어 브라우저 표기로 고정 */
export const formatFncoAmount = (n: number): string =>
  (Number.isFinite(n) ? n : 0).toLocaleString('ko-KR');

export const checkFncoPreMarketingBeforeSave = (
  state: FncoPreMarketingState | null | undefined,
): string | null =>
  state?.analysisId && state.analysis
    ? null
    : '마케팅 분석 결과를 불러오지 못했습니다.';

/** 편집·조회 — 저장 attributes.data 는 서버가 채운 분석 레코드 스냅샷 */
export const parseFncoPreMarketingAttributes = (
  attributes: unknown,
): FncoPreMarketingState | null => {
  const a = asRecord(attributes);
  const analysisId = asText(a?.analysisId);
  if (!analysisId) return null;
  const data = asRecord(a?.data);
  return { analysisId, analysis: data as FncoMarketingAnalysis | null };
};

/** data·internalOrderNumber 는 서버 소유(요청에 실으면 422) */
export const buildFncoPreMarketingAttributes = (
  analysisId: string,
  content: string,
): ApprovalAttributes => ({ analysisId, content });

/** 조회 화면 상신 — 단건조회 attributes 에서 요청 가능한 값만 */
export const pickFncoPreMarketingRequestAttributes = (
  attributes: unknown,
): ApprovalAttributes | null => {
  const a = asRecord(attributes);
  if (!a) return null;
  return buildFncoPreMarketingAttributes(
    asText(a.analysisId),
    asText(a.content),
  );
};
